/** QuickRouter — Symboles de schéma, déduits de l'empreinte du composant.
 *
 * Unités : mm, Y vers le haut, grille de 2,54 mm. Un symbole = des formes (traits, rectangle,
 * cercles, textes) et des broches : { num, name, pos (bout de la broche = point de connexion),
 * dir (L/R/U/D, vers l'extérieur), len }. Formes dédiées : résistance, condensateur, inductance,
 * fusible, diode, LED, MOSFET, bouton, connecteur ; sinon un boîtier rectangulaire (CI, module).
 * Les trous de fixation n'ont pas de symbole.
 */

export const G = 2.54;
const r3 = (v) => Math.round(v * 1000) / 1000;
const snap = (v) => Math.round(v / G) * G;

/** Broches distinctes d'une empreinte (une par numéro), dans l'ordre naturel des numéros. */
export function uniquePins(fp) {
  const seen = new Map();
  for (const p of fp.pads) if (!seen.has(p.num)) seen.set(p.num, p.name || '');
  return [...seen.entries()]
    .map(([num, name]) => ({ num, name }))
    .sort((a, b) => a.num.localeCompare(b.num, 'fr', { numeric: true }));
}

const line = (...pts) => ({ kind: 'line', pts });

function twoPin(pins, body, { left, right } = {}) {
  const [a, b] = left && right ? [pins.find((p) => p.name === left) ?? pins[0], pins.find((p) => p.name === right) ?? pins[1]] : pins;
  return {
    pins: [
      { num: a.num, name: a.name, pos: [-2 * G, 0], dir: 'L', len: G, hideName: true },
      { num: b.num, name: b.name, pos: [2 * G, 0], dir: 'R', len: G, hideName: true },
    ],
    shapes: body,
    box: [[-2 * G, -G], [2 * G, G]],
    refAt: [0, 1.9], valueAt: [0, -1.9],
  };
}

const RES = [{ kind: 'rect', pts: [[-2.3, -0.9], [2.3, 0.9]] }, line([-G, 0], [-2.3, 0]), line([2.3, 0], [G, 0])];
const CAP = [line([-G, 0], [-0.5, 0]), line([-0.5, -1.6], [-0.5, 1.6]), line([0.5, -1.6], [0.5, 1.6]), line([0.5, 0], [G, 0])];
const IND = [line([-G, 0], [-2, 0]), { kind: 'arcs', pts: [[-2, 0], [2, 0]], n: 4 }, line([2, 0], [G, 0])];
const FUSE = [{ kind: 'rect', pts: [[-2.3, -0.7], [2.3, 0.7]] }, line([-G, 0], [G, 0])];
const DIODE = [line([-G, 0], [-1.1, 0]), { kind: 'poly', pts: [[-1.1, -1.3], [-1.1, 1.3], [1.1, 0], [-1.1, -1.3]] }, line([1.1, -1.3], [1.1, 1.3]), line([1.1, 0], [G, 0])];
const LED = [...DIODE, line([0.2, 1.5], [1.4, 2.7]), line([1.4, 2.7], [0.8, 2.6]), line([1.0, 1.4], [2.2, 2.6]), line([2.2, 2.6], [1.6, 2.5])];
const SWITCH = [line([-G, 0], [-1.6, 0]), { kind: 'circle', c: [-1.6, 0], r: 0.3 }, { kind: 'circle', c: [1.6, 0], r: 0.3 }, line([1.6, 0], [G, 0]), line([-1.4, 0.4], [1.6, 1.4]), line([0, 1.0], [0, 2.0]), line([-0.8, 2.0], [0.8, 2.0])];

function mosfet(pins) {
  const by = (n) => pins.find((p) => p.name === n);
  const g = by('G'), d = by('D'), s = by('S');
  return {
    pins: [
      { num: g.num, name: 'G', pos: [-2 * G, 0], dir: 'L', len: G, hideName: true },
      { num: d.num, name: 'D', pos: [G, 2 * G], dir: 'U', len: G, hideName: true },
      { num: s.num, name: 'S', pos: [G, -2 * G], dir: 'D', len: G, hideName: true },
    ],
    shapes: [
      line([-G, 0], [-0.6, 0]), line([-0.6, -1.6], [-0.6, 1.6]),
      line([0, -1.9], [0, -1.0]), line([0, -0.4], [0, 0.4]), line([0, 1.0], [0, 1.9]),
      line([0, 1.45], [G, 1.45]), line([G, 1.45], [G, G]),
      line([0, -1.45], [G, -1.45]), line([G, -1.45], [G, -G]),
      line([0, 0], [G, 0]), line([G, 0], [G, -1.45]),
      { kind: 'poly', pts: [[0.1, 0], [0.9, 0.45], [0.9, -0.45], [0.1, 0]], fill: true },
      { kind: 'circle', c: [0.9, 0], r: 2.9 },
    ],
    box: [[-2 * G, -2 * G], [G + 3, 2 * G]],
    refAt: [4.6, 1.2], valueAt: [4.6, -1.2], anchor: 'start',
  };
}

/** Connecteur : broches à gauche, numéro et nom dans le corps. */
function connector(pins) {
  const n = pins.length;
  const top = ((n - 1) * G) / 2;
  const w = Math.max(2 * G, snap(Math.max(...pins.map((p) => (p.name || p.num).length)) * 1.15 + 3.5));
  const out = pins.map((p, k) => ({ num: p.num, name: p.name || p.num, pos: [-G, r3(snap(top) - k * G)], dir: 'L', len: G }));
  const yTop = snap(top) + G, yBot = snap(top) - (n - 1) * G - G;
  return {
    pins: out,
    shapes: [{ kind: 'rect', pts: [[0, yBot], [w, yTop]] }],
    box: [[-G, yBot], [w, yTop]],
    refAt: [w / 2, yTop + 1.4], valueAt: [w / 2, yBot - 1.4],
    pinNamesInside: true,
  };
}

/** Boîtier générique : broches 1…n/2 à gauche (haut → bas), les suivantes à droite (bas → haut). */
function ic(pins) {
  const n = pins.length;
  const left = pins.slice(0, Math.ceil(n / 2));
  const right = pins.slice(Math.ceil(n / 2)).reverse();
  const rows = Math.max(left.length, right.length);
  const nameW = (list) => Math.max(0, ...list.map((p) => (p.name || p.num).length)) * 1.05;
  const w = Math.max(4 * G, snap(nameW(left) + nameW(right) + 4));
  const yTop = (rows + 1) * G, x1 = w; // une rangée de marge : la dernière broche ne tombe pas sur le bord
  const out = [
    ...left.map((p, k) => ({ num: p.num, name: p.name, pos: [-G, r3(yTop - (k + 1) * G)], dir: 'L', len: G })),
    ...right.map((p, k) => ({ num: p.num, name: p.name, pos: [r3(x1 + G), r3(yTop - (k + 1) * G)], dir: 'R', len: G })),
  ];
  return {
    pins: out,
    shapes: [{ kind: 'rect', pts: [[0, 0], [x1, yTop]] }],
    box: [[-G, 0], [x1 + G, yTop]],
    refAt: [x1 / 2, yTop + 1.4], valueAt: [x1 / 2, -1.4],
  };
}

const cache = new WeakMap();

/** Symbole d'une empreinte (null pour les trous de fixation). */
export function symbolFor(fp) {
  if (!fp) return null;
  if (cache.has(fp)) return cache.get(fp);
  const pins = uniquePins(fp);
  const pre = fp.refPrefix || 'U';
  const names = new Set(pins.map((p) => p.name));
  let sym;
  if (pre === 'H') sym = null;
  else if (pins.length === 2 && pre === 'R') sym = twoPin(pins, RES);
  else if (pins.length === 2 && pre === 'C') sym = twoPin(pins, CAP);
  else if (pins.length === 2 && pre === 'L') sym = twoPin(pins, IND);
  else if (pins.length === 2 && pre === 'F') sym = twoPin(pins, FUSE);
  else if (pins.length === 2 && pre === 'D') sym = twoPin(pins, /led/i.test(fp.id + fp.name) || /^d-0/.test(fp.id) ? LED : DIODE, names.has('A') && names.has('K') ? { left: 'A', right: 'K' } : {});
  else if (pins.length === 2 && pre === 'SW') sym = twoPin(pins, SWITCH);
  else if (pins.length === 3 && names.has('G') && names.has('S') && names.has('D')) sym = mosfet(pins);
  else if (pre === 'J') sym = connector(pins);
  else sym = ic(pins);
  if (sym) sym.kindName = pre;
  cache.set(fp, sym);
  return sym;
}

// ── Placement ──────────────────────────────────────────────────

const DIRS = ['R', 'U', 'L', 'D'];

export function placeSym(pl, [x, y]) {
  const px = pl.mirror ? -x : x;
  const a = ((pl.rot || 0) * Math.PI) / 180;
  const c = Math.round(Math.cos(a)), s = Math.round(Math.sin(a));
  return [r3(pl.pos[0] + px * c - y * s), r3(pl.pos[1] + px * s + y * c)];
}

export function placeDir(pl, dir) {
  let k = DIRS.indexOf(dir);
  if (pl.mirror && (dir === 'L' || dir === 'R')) k = (k + 2) % 4;
  return DIRS[(k + Math.round((pl.rot || 0) / 90)) % 4];
}

/** Broches d'un symbole posé, en coordonnées du schéma. */
export function placedPins(pl, sym) {
  if (!sym) return [];
  return sym.pins.map((p) => ({ ...p, pos: placeSym(pl, p.pos), dir: placeDir(pl, p.dir) }));
}

/** Rectangle englobant d'un symbole posé. */
export function placedBox(pl, sym) {
  const [[x0, y0], [x1, y1]] = sym.box;
  const pts = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map((p) => placeSym(pl, p));
  return [Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[1]))];
}
